import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, TrendingUp, BarChart3, Target, ArrowUpRight, Clock, CheckCircle2 } from 'lucide-react';

const fmt = (v) => v ? `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '$0';

export default function EarningsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/earnings')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid="earnings-page" className="space-y-5">
        <h1 className="text-3xl font-bold text-gray-900">Earnings</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28 bg-gray-200 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Skeleton className="h-64 bg-gray-200 rounded-xl" />
          <Skeleton className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!data) return <p className="text-gray-500">Failed to load earnings data.</p>;

  const monthlyEntries = Object.entries(data.monthly_earnings || {}).sort(([a], [b]) => a.localeCompare(b));
  const maxMonthly = Math.max(...monthlyEntries.map(([, v]) => v), 1);

  return (
    <div data-testid="earnings-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Earnings</h1>
        <p className="text-gray-500 mt-1">Track your assignment fees and projected income</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testId="kpi-total-earned"
          label="Total Earned"
          value={fmt(data.total_earned)}
          sub={`${data.total_deals_closed} deal${data.total_deals_closed !== 1 ? 's' : ''} closed`}
          icon={DollarSign}
          color="emerald"
        />
        <KpiCard
          testId="kpi-projected"
          label="Projected Earnings"
          value={fmt(data.projected_earnings)}
          sub={`${data.pipeline_count} deal${data.pipeline_count !== 1 ? 's' : ''} in pipeline`}
          icon={TrendingUp}
          color="blue"
        />
        <KpiCard
          testId="kpi-avg-fee"
          label="Avg. Assignment Fee"
          value={fmt(data.avg_fee_closed)}
          sub="Per closed deal"
          icon={BarChart3}
          color="purple"
        />
        <KpiCard
          testId="kpi-under-contract"
          label="Under Contract"
          value={fmt(data.under_contract_value)}
          sub="Pending close"
          icon={Target}
          color="amber"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Monthly Earnings Bar Chart */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Monthly Earnings</h3>
            {monthlyEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <BarChart3 className="h-10 w-10 text-gray-300 mb-3" strokeWidth={1.5} />
                <p className="text-sm text-gray-400">No closed deals yet</p>
                <p className="text-xs text-gray-400 mt-1">Close your first deal to see earnings here</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {monthlyEntries.map(([month, value]) => {
                  const pct = (value / maxMonthly) * 100;
                  const label = new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  return (
                    <div key={month} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-16 shrink-0">{label}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full flex items-center px-2 transition-all duration-500"
                          style={{ width: `${Math.max(pct, 8)}%` }}
                        >
                          <span className="text-[10px] font-bold text-white whitespace-nowrap">{fmt(value)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pipeline Breakdown */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Earnings Breakdown</h3>
            <div className="space-y-3">
              <BreakdownRow
                label="Closed Deals"
                value={fmt(data.total_earned)}
                count={data.total_deals_closed}
                icon={CheckCircle2}
                color="emerald"
              />
              <BreakdownRow
                label="Under Contract (Pending)"
                value={fmt(data.under_contract_value)}
                count={data.pipeline_deals.filter(d => d.status === 'Under Contract').length}
                icon={Clock}
                color="indigo"
              />
              <BreakdownRow
                label="Marketed Deals"
                value={fmt(data.marketed_value)}
                count={data.pipeline_deals.filter(d => d.status === 'Marketed').length}
                icon={ArrowUpRight}
                color="cyan"
              />
              <BreakdownRow
                label="Total Pipeline"
                value={fmt(data.projected_earnings)}
                count={data.pipeline_count}
                icon={TrendingUp}
                color="blue"
              />
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">Total (Earned + Projected)</span>
                  <span className="text-lg font-bold font-mono text-gray-900">{fmt(data.total_earned + data.projected_earnings)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent Closed Deals */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Closed Deals</h3>
            {data.recent_closed.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No closed deals yet</p>
            ) : (
              <div className="space-y-2">
                {data.recent_closed.map(d => (
                  <div
                    key={d.id}
                    data-testid={`closed-deal-${d.id}`}
                    onClick={() => navigate(`/leads/${d.id}`)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-gray-100"
                  >
                    <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{d.address || d.name}</p>
                      <p className="text-xs text-gray-500">{d.city}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-emerald-600 font-mono">{fmt(d.assignment_fee)}</p>
                      <p className="text-[10px] text-gray-400">Fee</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pipeline Deals (Projected) */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Pipeline Deals (Projected Fees)</h3>
            {data.pipeline_deals.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No deals in pipeline</p>
            ) : (
              <div className="space-y-2">
                {data.pipeline_deals.map(d => {
                  const statusColor = {
                    'Offer Sent': 'bg-purple-50 text-purple-600',
                    'Follow-Up': 'bg-amber-50 text-amber-600',
                    'Price Watch': 'bg-pink-50 text-pink-600',
                    'Rejected Countered': 'bg-orange-50 text-orange-600',
                    'Rejected NC': 'bg-rose-50 text-rose-600',
                    'Accepted Other Offer': 'bg-slate-100 text-slate-600',
                    'Under Contract': 'bg-indigo-50 text-indigo-600',
                    'Marketed': 'bg-cyan-50 text-cyan-600',
                  };
                  return (
                    <div
                      key={d.id}
                      data-testid={`pipeline-deal-${d.id}`}
                      onClick={() => navigate(`/leads/${d.id}`)}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-gray-100"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{d.address || d.name}</p>
                        <p className="text-xs text-gray-500">{d.city}</p>
                      </div>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusColor[d.status] || 'bg-gray-100 text-gray-600'}`}>
                        {d.status}
                      </span>
                      <div className="text-right shrink-0 min-w-[70px]">
                        <p className="text-sm font-bold text-blue-600 font-mono">{fmt(d.projected_fee)}</p>
                        <p className="text-[10px] text-gray-400">Projected</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ testId, label, value, sub, icon: Icon, color }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
  };
  const iconColorMap = {
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
  };
  return (
    <Card data-testid={testId} className={`border shadow-sm ${colorMap[color]}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${iconColorMap[color]}`}>
            <Icon className="h-4.5 w-4.5" strokeWidth={2} />
          </div>
        </div>
        <p className="text-2xl font-bold font-mono text-gray-900">{value}</p>
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
      </CardContent>
    </Card>
  );
}

function BreakdownRow({ label, value, count, icon: Icon, color }) {
  const iconColors = {
    emerald: 'text-emerald-500',
    indigo: 'text-indigo-500',
    cyan: 'text-cyan-500',
    blue: 'text-blue-500',
  };
  return (
    <div className="flex items-center gap-3">
      <Icon className={`h-4 w-4 shrink-0 ${iconColors[color]}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700">{label}</p>
      </div>
      <span className="text-xs text-gray-400">{count} deal{count !== 1 ? 's' : ''}</span>
      <span className="text-sm font-semibold font-mono text-gray-900 min-w-[80px] text-right">{value}</span>
    </div>
  );
}
